export interface Agency {
  id: string;
  name: string;
  description: string;
  contactEmail: string;
  isActive: boolean;
  parentAgencyId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface SubAgency {
  id: string;
  name: string;
  description: string;
  contactEmail: string;
  isActive: boolean;
  parentAgencyId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LeaderboardStats {
  totalPremium: number;
  totalSales: number;
  conversionRate: number;
  wonLeads?: number;
  lostLeads?: number;
  underwritingLeads?: number;
  /** % of the effective goal reached in its own window; null = no goal. */
  goalProgress?: number | null;
  goalLabel?: string | null;
  leadsConverted: number;
  appointments: number;
  callsMade: number;
  activeClients: number;
  totalPolicies?: number; // Total policies count from backend
  period: {
    startDate: string;
    endDate: string;
  };
}

export interface LeaderboardAgent {
  userId: string;
  name: string;
  department: string;
  agency: Agency | null;
  subAgency: SubAgency | null;
  totalPremium: number;
  /** Sold & Approved only — issued/net production. totalPremium is gross and
   *  also includes Underwriting, matching the agency dashboard's two boxes. */
  issuedPremium?: number;
  totalSales: number;
  conversionRate: number;
  wonLeads?: number;
  lostLeads?: number;
  underwritingLeads?: number;
  /** % of the effective goal reached in its own window; null = no goal. */
  goalProgress?: number | null;
  goalLabel?: string | null;
  leadsConverted: number;
  appointments: number;
  callsMade: number;
  activeClients: number;
  visibility: boolean;
  rank: number;
  // Add these properties that your components need
  id?: string | number;
  avatar?: string;
  totalLeads?: number;
  avgCallDuration?: number;
  joinDate?: string;
  isOnline?: boolean;
  badgeType?: "gold" | "silver" | "bronze";
  showInLeaderboard?: boolean;
  totalPolicies?: number; // Total policies count from backend
  // Profile picture fields
  profileImageUrl?: string;
  profile_image_url?: string; // Snake case for backend compatibility
  profileImagePath?: string;
  profile_image_path?: string; // Snake case for backend compatibility
}

export interface LeaderboardResponse {
  statusCode: number;
  message: string;
  stats: LeaderboardStats;
  leaderboard: LeaderboardAgent[];
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

/**
 * The two numbers behind a closing ratio — "8 closed · 0 lost" — so 100% on
 * one lead reads as what it is. Closed = Sold & Approved + Underwriting.
 */
export function decisionFraction(a: { wonLeads?: number; lostLeads?: number; underwritingLeads?: number }): string | null {
  if (a.wonLeads == null || a.lostLeads == null || a.underwritingLeads == null) return null;
  const closed = a.wonLeads + a.underwritingLeads;
  if (closed + a.lostLeads === 0) return "no sales or losses yet";
  return `${closed} closed · ${a.lostLeads} lost`;
}
